import * as THREE from "three";
import type { Grabbable } from "./grabbable";

/** Result of attempting to seat a grabbable into a target. */
export type PlacementResult =
  | { ok: true; settled: true }
  | {
      ok: false;
      reason:
        | "wrong_target"
        | "out_of_range"
        | "occupied"
        | "locked"
        | "gated"
        | "invalid";
    };

export interface PlacementTarget {
  object: THREE.Object3D;
  id: string;
  /** When set, only grabbables whose `accepts` matches may lock here. */
  accepts: string | null;
  /** World-space snap radius in meters (training-forgiving). */
  snapRadius: number;
  /**
   * First occupant (same as `occupants[0]`). Outlet wires stay single-seat
   * (`maxOccupants: 1`); jumper circuit posts may stack.
   */
  occupiedBy: Grabbable | null;
  /** All seated occupants. Source of truth for capacity / unplug. */
  occupants: Grabbable[];
  /** Max simultaneous plugs. Default 1 so outlet terminals stay exclusive. */
  maxOccupants: number;
  /** Local pose applied to the grabbable when locked (child of target). */
  snapLocalPosition: THREE.Vector3;
  snapLocalQuaternion: THREE.Quaternion;
  /** Optional materials on the target for confirm / reject pulses. */
  feedbackMats: THREE.MeshStandardMaterial[];
}

export interface CreatePlacementTargetOptions {
  id: string;
  object: THREE.Object3D;
  accepts?: string | null;
  snapRadius?: number;
  snapLocalPosition?: THREE.Vector3;
  snapLocalQuaternion?: THREE.Quaternion;
  /** Default 1. Circuit jumper posts use 2 so parallel rails can share a node. */
  maxOccupants?: number;
}

/** Default wire→terminal snap radius (~8 cm). */
export const DEFAULT_WIRE_SNAP_RADIUS = 0.08;
/** Default receptacle→box snap radius (~10 cm). */
export const DEFAULT_MOUNT_SNAP_RADIUS = 0.1;

export function createPlacementTarget(
  opts: CreatePlacementTargetOptions,
): PlacementTarget {
  const feedbackMats: THREE.MeshStandardMaterial[] = [];
  opts.object.traverse((node) => {
    const mesh = node as THREE.Mesh;
    if (!mesh.isMesh) return;
    const mats = Array.isArray(mesh.material) ? mesh.material : [mesh.material];
    for (const m of mats) {
      if (m instanceof THREE.MeshStandardMaterial) feedbackMats.push(m);
    }
  });

  return {
    object: opts.object,
    id: opts.id,
    accepts: opts.accepts ?? null,
    snapRadius: opts.snapRadius ?? DEFAULT_WIRE_SNAP_RADIUS,
    occupiedBy: null,
    occupants: [],
    maxOccupants: Math.max(1, opts.maxOccupants ?? 1),
    snapLocalPosition: (opts.snapLocalPosition ?? new THREE.Vector3()).clone(),
    snapLocalQuaternion: (
      opts.snapLocalQuaternion ?? new THREE.Quaternion()
    ).clone(),
    feedbackMats,
  };
}

const _anchorWorld = new THREE.Vector3();
const _targetWorld = new THREE.Vector3();

/** Local-X spacing between stacked plugs when `maxOccupants > 1` (meters). */
const STACK_SPACING_M = 0.04;
/** Tighter pitch when a bus holds many plugs (room srcP = 5). */
const STACK_SPACING_BUS_M = 0.028;

/**
 * Local-X offset for a stacked plug.
 * - Pair posts (`maxOccupants <= 2`): center the pair (existing SP rails).
 * - High-occupancy buses: keep slot 0 on the post axis so a single seated
 *   jumper (room Light A at boot) does not jump sideways.
 */
function stackOffsetX(slot: number, maxOccupants: number): number {
  if (maxOccupants <= 2) {
    const centered = slot - (maxOccupants - 1) / 2;
    return centered * STACK_SPACING_M;
  }
  const spacing = STACK_SPACING_BUS_M;
  if (slot === 0) return 0;
  const signed = Math.ceil(slot / 2) * (slot % 2 === 1 ? 1 : -1);
  return signed * spacing;
}

function syncOccupiedBy(target: PlacementTarget): void {
  target.occupiedBy = target.occupants[0] ?? null;
}

/** True when this grabbable is seated on the target (including stacked posts). */
export function isOccupiedBy(
  target: PlacementTarget,
  grabbable: Grabbable,
): boolean {
  return target.occupants.includes(grabbable);
}

/** Remove one occupant without clearing stacked neighbors. */
export function releaseOccupant(
  target: PlacementTarget,
  grabbable: Grabbable,
): void {
  const i = target.occupants.indexOf(grabbable);
  if (i >= 0) target.occupants.splice(i, 1);
  delete grabbable.object.userData.placementSlot;
  syncOccupiedBy(target);
}

/**
 * Role used against {@link PlacementTarget.accepts}.
 * Wires match by `wireId` (hot/neutral/ground); everything else by `kind`
 * (receptacle, jumper-end, …).
 */
function placementRole(grabbable: Grabbable): string | null {
  const data = grabbable.object.userData as {
    wireId?: unknown;
    kind?: unknown;
  };
  if (typeof data.wireId === "string") return data.wireId;
  if (typeof data.kind === "string") return data.kind;
  return null;
}

/** Distance from grabbable place-anchor to target origin. */
export function placementDistance(
  grabbable: Grabbable,
  target: PlacementTarget,
): number {
  grabbable.placeAnchor.getWorldPosition(_anchorWorld);
  target.object.getWorldPosition(_targetWorld);
  return _anchorWorld.distanceTo(_targetWorld);
}

export interface EvaluatePlacementOptions {
  requireAcceptMatch?: boolean;
  /**
   * When true (desktop click-to-place), skip the world-space snap radius and
   * only enforce accept/occupied rules. XR release keeps radius checks.
   */
  ignoreDistance?: boolean;
}

/**
 * Evaluate whether `grabbable` may lock into `target` at the current pose.
 * Does not mutate — caller applies snap/reject.
 */
export function evaluatePlacement(
  grabbable: Grabbable,
  target: PlacementTarget,
  opts?: EvaluatePlacementOptions,
): PlacementResult {
  if (grabbable.locked) return { ok: false, reason: "locked" };

  const others = target.occupants.filter((g) => g !== grabbable);
  const jumperId = grabbable.object.userData.jumperId;
  if (typeof jumperId === "string") {
    for (const occ of others) {
      if (occ.object.userData.jumperId === jumperId) {
        // Same jumper cannot seat both ends on one terminal.
        return { ok: false, reason: "invalid" };
      }
    }
  }
  if (others.length >= target.maxOccupants) {
    return { ok: false, reason: "occupied" };
  }

  const ignoreDistance = opts?.ignoreDistance ?? false;
  const inRange =
    ignoreDistance ||
    placementDistance(grabbable, target) <= target.snapRadius;

  const requireMatch = opts?.requireAcceptMatch ?? true;
  if (requireMatch && target.accepts != null) {
    const role = placementRole(grabbable);
    if (role !== target.accepts) {
      // Wrong role: report rejection when in range (or desktop click).
      if (inRange) return { ok: false, reason: "wrong_target" };
      return { ok: false, reason: "out_of_range" };
    }
  }

  if (!inRange) return { ok: false, reason: "out_of_range" };

  return { ok: true, settled: true };
}

/**
 * Parent grabbable under target at the configured local snap pose and mark locked.
 */
export function lockIntoTarget(
  grabbable: Grabbable,
  target: PlacementTarget,
): void {
  target.object.attach(grabbable.object);
  grabbable.object.position.copy(target.snapLocalPosition);
  grabbable.object.quaternion.copy(target.snapLocalQuaternion);
  grabbable.object.scale.set(1, 1, 1);

  if (target.maxOccupants > 1) {
    const used = new Set<number>();
    for (const occ of target.occupants) {
      if (occ === grabbable) continue;
      const slot = occ.object.userData.placementSlot;
      if (typeof slot === "number") used.add(slot);
    }
    let slot = 0;
    while (used.has(slot) && slot < target.maxOccupants) slot++;
    grabbable.object.userData.placementSlot = slot;
    grabbable.object.position.x += stackOffsetX(slot, target.maxOccupants);
  } else {
    delete grabbable.object.userData.placementSlot;
  }

  grabbable.locked = true;
  grabbable.enabled = false;
  if (!target.occupants.includes(grabbable)) {
    target.occupants.push(grabbable);
  }
  syncOccupiedBy(target);
}

/** Clear all occupancy (object restore is caller's job). */
export function clearTarget(target: PlacementTarget): void {
  target.occupants.length = 0;
  target.occupiedBy = null;
}

/**
 * Pick the nearest valid-or-rejectable target within its snap radius.
 * Prefers a correct match when multiple overlap.
 */
function isRejectableReason(
  reason: Exclude<PlacementResult, { ok: true }>["reason"],
): boolean {
  return (
    reason === "wrong_target" ||
    reason === "occupied" ||
    reason === "invalid"
  );
}

export function findBestTarget(
  grabbable: Grabbable,
  targets: readonly PlacementTarget[],
  opts?: EvaluatePlacementOptions,
): { target: PlacementTarget; result: PlacementResult } | null {
  let bestReject: {
    target: PlacementTarget;
    result: PlacementResult;
    dist: number;
  } | null = null;
  let bestOk: { target: PlacementTarget; dist: number } | null = null;

  for (const target of targets) {
    const dist = opts?.ignoreDistance
      ? 0
      : placementDistance(grabbable, target);
    if (!opts?.ignoreDistance && dist > target.snapRadius) continue;
    const result = evaluatePlacement(grabbable, target, opts);
    if (result.ok) {
      if (!bestOk || dist < bestOk.dist) bestOk = { target, dist };
    } else if (isRejectableReason(result.reason)) {
      if (!bestReject || dist < bestReject.dist) {
        bestReject = { target, result, dist };
      }
    }
  }

  if (bestOk) {
    return {
      target: bestOk.target,
      result: { ok: true, settled: true },
    };
  }
  if (bestReject) {
    return {
      target: bestReject.target,
      result: bestReject.result,
    };
  }
  return null;
}
